// @/hooks/useContentSettings.js
import { useStaticQuery, graphql } from 'gatsby';
import { useLanguage } from '@/components/providers/LanguageContext';
import { useState, useEffect } from 'react';
import { contentStore } from '@/services/app/content';

/**
 * Hook to fetch Account Settings content from Gatsby's GraphQL layer
 * Uses language context to automatically update content when language changes
 * 
 * @param {string} [fallbackLocale='en'] - Fallback locale if context not available or locale not found
 * @returns {Object|null} Content for requested locale or null if no content exists at all
 */
export const useContentSettings = (fallbackLocale = 'en') => {
  // Get current locale from context or use fallback
  const { locale } = useLanguage?.() || { locale: fallbackLocale };

  // Track content for dynamic updates
  const [content, setContent] = useState(null);

  // Execute the GraphQL query
  const data = useStaticQuery(graphql`
    query {
      allContentSettings {
        nodes {
          id
          # Page Layout
          pageTitle
          pageSubtitle
          
          # Tabs Navigation
          personalSettingsTabLabel
          displaySettingsTabLabel
          languageSettingsTabLabel
          securitySettingsTabLabel
          
          # Personal Settings Section
          personalInfoSectionTitle
          mainUserInfoText
          contactDetailsSectionTitle
          contactDetailsSectionDesc
          saveChangesButtonText
          savingButtonText
          
          # Loading States - both new and legacy fields
          loadingMessage
          savingMessage
          loadingProfileMessage
          savingProfileMessage
          
          # Display Settings Section
          displayPreferencesSectionTitle
          colorModeLabel
          lightModeOptionText
          darkModeOptionText
          systemModeOptionText
          
          # Language Settings Section
          languagePreferencesSectionTitle
          languagePreferencesDescription
          languageChangeSuccessTitle
          languageChangeSuccessTemplate
          languageNotAvailableNote
          
          # Security Settings Section
          securitySettingsSectionTitle
          securityPortalDescription
          securityLinkText
          
          # Form Fields
          firstNameLabel
          lastNameLabel
          emailLabel
          emailReadOnlyMessage
          jobTitleLabel
          phoneNumberLabel
          phoneNumberHint
          requiredFieldIndicator
          
          # Validation Messages
          firstNameRequiredError
          lastNameRequiredError
          invalidPhoneError
          
          # Notifications
          profileUpdateSuccessTitle
          profileUpdateSuccessMessage
          updateErrorTitle
          genericErrorMessage

          analyticsSettingSectionTitle
          analyticsSettingDescription
          cookieBannerTitle
          cookieBannerDescription
          cookieBannerAcceptButtonText
          cookieBannerDenyButtonText
          cookieBannerPrivacyPolicyText
          
          locale
        }
      }
    }
  `);

  // Update content when locale changes
  useEffect(() => {
    const nodes = data.allContentSettings?.nodes || [];

    // First try the requested locale
    const localeContent = nodes.find(node => node.locale === locale);

    // Fall back to English if the requested locale isn't available
    const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

    // Check if we have GraphQL content
    if (localeContent || englishContent) {
      setContent(localeContent || englishContent || null);
    } else {
      // Try to get content from the store as a fallback
      try {
        const storeContent = contentStore.getSettingsContent(locale);
        if (storeContent) {
          setContent(storeContent);
        } else {
          setContent(null);
        }
      } catch (err) {
        setContent(null);
      }
    }
  }, [locale, data.allContentSettings?.nodes]);

  // Register effect for content change events
  useEffect(() => {
    const handleContentRefresh = (event) => {
      if (event.detail?.locale) {
        // Force refresh content when an external event requests it
        try {
          const storeContent = contentStore.getSettingsContent(event.detail.locale);
          if (storeContent) {
            setContent(storeContent);
          }
        } catch (err) {
          // Silent error handling
        }
      }
    };

    // Listen for content refresh events
    window.addEventListener('forceContentRefresh', handleContentRefresh);
    window.addEventListener('forceSettingsRefresh', handleContentRefresh);

    // Clean up listeners
    return () => {
      window.removeEventListener('forceContentRefresh', handleContentRefresh);
      window.removeEventListener('forceSettingsRefresh', handleContentRefresh);
    };
  }, []);

  return content;
};

/**
 * Fallback version for components that don't have access to the language context
 * @param {string} locale - Desired locale (defaults to 'en')
 * @returns {Object|null} Content for requested locale or null
 */
export const getContentSettingsForLocale = (locale = 'en') => {
  // First try to get content from the store
  try {
    const storeContent = contentStore.getSettingsContent(locale);
    if (storeContent) {
      return storeContent;
    }
  } catch (err) {
    // Silent error handling
  }

  // Fall back to Gatsby data if store fails
  try {
    if (typeof window !== 'undefined' && window.__GATSBY_INITIAL_DATA__) {
      // Attempt to access GraphQL data from Gatsby
      const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults || {};

      for (const queryId in staticQueries) {
        const queryResult = staticQueries[queryId];
        const nodes = queryResult?.data?.allContentSettings?.nodes || [];

        if (nodes.length > 0) {
          // First try the requested locale
          const localeContent = nodes.find(node => node.locale === locale);

          // Fall back to English if the requested locale isn't available
          const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

          return localeContent || englishContent || null;
        }
      }
    }
  } catch (error) {
    // Silent error handling
  }

  return null;
};